import React from 'react';
import { useSelector } from 'react-redux';

import { TranslateBadge } from '@travel/icons/ui';
import { useGetTranslation } from '@travel/traveler-core/hooks';
import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';
import { IconTextList } from '@travel/ui';
import { cx, isEmptyArray, isNotEmptyArray } from '@travel/utils';

import GallerySlider from 'components/GallerySlider';
import { mappedIconTextListData } from 'pages/ProviderInformationPage/helpers';

import { getIsDirectContractType } from 'store/providerInformation/selectors';
import { getRoomDetail } from 'store/providerRatePlan/selectors';

import type { RoomDetail } from 'ProviderRatePlan-Types';

import RoomBasicInfoSummary from '../RoomBasicInfoSummary';

import styles from './itemDetails.module.scss';

type Props = {
  className?: string;
  /** String of room item id */
  itemId: string;
  /** Name on what page this component is on for RAT */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
};

function ItemDetails(props: Props) {
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'sp';

  const getTranslation = useGetTranslation();
  const isDirectContractType = useSelector(getIsDirectContractType);

  const room = useSelector(
    getRoomDetail(props.itemId, getTranslation, isDirectContractType),
  ) as RoomDetail;

  return (
    <div className={styles.dialogContainer} data-testid="itemDetail-dialogContent">
      <GallerySlider
        onPage={props.onPage}
        className={cx(styles.mediaSlider, styles.mediaSize)}
        mediaClassName={styles.mediaSize}
        videoClassName={styles.mediaVideo}
        isShowGalleryOnClick={isMobile}
        media={room.media}
        mediaSizes={isMobile ? '100vw' : '30vw'}
      />
      <div className={styles.detailInfoWrapper}>
        <div className={styles.detailInfoContainer}>
          <p
            className={cx(styles.name, styles.info)}
            data-testid="itemDetail-dialogContent-roomName"
          >
            {room.name}
          </p>
          <div className={styles.planDetail} data-testid="itemDetail-dialogContent-planDetail">
            <RoomBasicInfoSummary
              bedText={room.bedText}
              roomText={room.roomText}
              smokingAllowed={room.smokingAllowed}
              capacityText={room.capacityText}
              viewText={room.views?.text}
            />
            {room.isGoogleTranslated && (
              <TranslateBadge
                data-testid="itemDetail-dialogContent-planDetail-translateBadge"
                className={styles.planDetailTranslateBadge}
              />
            )}
          </div>
        </div>
        {/* facilities */}
        {isNotEmptyArray(room.facilities) &&
          room.facilities.map(({ group, features }) => {
            const filteredFeatures = mappedIconTextListData(features, styles.featureIcon);
            if (isEmptyArray(filteredFeatures)) {
              return null;
            }

            return (
              <div
                className={styles.detailInfoContainer}
                key={group}
                data-testid="itemDetail-dialogContent-detailInfoContainer"
              >
                <div className={styles.containerTitle}>{group}</div>
                <IconTextList
                  className={styles.featureItemContainer}
                  iconTextClassName={styles.featureItem}
                  iconTextSpacing={4}
                  spaceBetween={0}
                  items={filteredFeatures}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default ItemDetails;
